import classNames from "classnames";
import { forwardRef } from "react";
import styles from "./Button.module.scss";
import Loader from "../Loader/Loader";

export type ButtonVariants =
  | "unstyled"
  | "primary"
  | "secondary"
  | "outline"
  | "gray"
  | "dark"
  | "link";

export type ButtonSize =
  | "xsmall"
  | "small"
  | "medium"
  | "large"
  | "xlarge"
  | "xxlarge";

export type ButtonProps = {
  variant?: ButtonVariants;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  loading?: boolean;
  size?: ButtonSize;
  className?: string;
  flat?: boolean;
  destructive?: boolean;
  asDiv?: boolean;
} & React.ComponentPropsWithoutRef<"button">;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "unstyled",
    children,
    leftIcon,
    rightIcon,
    className,
    loading,
    size = "medium",
    flat = false,
    destructive = false,
    asDiv,
    ...buttonProps
  },
  ref,
) {
  const { type } = buttonProps;

  if (asDiv) {
    return (
      <div
        className={classNames(
          styles.button,
          styles[
            !destructive ? `button-${variant}` : `button-${variant}-destructive`
          ],
          className,
          {
            [styles.disabled]: buttonProps.disabled,
            [styles.boxShadow]: !flat,
            [styles[`button-${size}`]]: variant !== "unstyled",
          },
        )}
      >
        {loading ? (
          <div className={styles.loader}>
            <Loader
              color={
                ["outline", "link"].includes(variant) ? "primary" : "white"
              }
              size="small"
            />
          </div>
        ) : null}
        <div
          className={classNames(styles.buttonContent, {
            [styles.loading]: loading,
          })}
        >
          {leftIcon ? <div className={styles.leftIcon}>{leftIcon}</div> : null}
          {children}
          {rightIcon ? (
            <div className={styles.rightIcon}>{rightIcon}</div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <button
      ref={ref}
      {...buttonProps}
      className={classNames(
        styles.button,
        styles[
          !destructive ? `button-${variant}` : `button-${variant}-destructive`
        ],
        className,
        {
          [styles.boxShadow]: !flat,
          [styles[`button-${size}`]]: variant !== "unstyled",
        },
      )}
      type={type ?? "button"}
    >
      {loading ? (
        <div className={styles.loader}>
          <Loader
            color={["outline", "link"].includes(variant) ? "primary" : "white"}
            size="small"
          />
        </div>
      ) : null}
      <div
        className={classNames(styles.buttonContent, {
          [styles.loading]: loading,
        })}
      >
        {leftIcon ? <div className={styles.leftIcon}>{leftIcon}</div> : null}
        {children}
        {rightIcon ? <div className={styles.rightIcon}>{rightIcon}</div> : null}
      </div>
    </button>
  );
});

export default Button;
